import React, { useCallback, useEffect, useState } from "react";
import CustomerSideMenu from "./customer-sidemenu/CustomerSidemenu";
import MobileCustomerMenu from "./customer-sidemenu/MobileCustomerMenu";
import MobileMenuButton from "./customer-sidemenu/MobileMenuButton";
import styles from "./CustomerLayout.module.scss";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { menuItems } from "./customer-sidemenu/CustomerMenu.constants";
import { logOut, updateCustomerInfo } from "../../stores/slices/current-user-slice";
import { ApplicationRoutes } from "../../routes";
import { useDispatch } from "react-redux";
import { useGetUserInfoQuery } from "../../service/user-service";
import { useNotificationHub } from "../../hooks/useNotificationHub";
import { NotificationListener } from "../../components/NotificationListener";
import { useGetUnreadNotificationsCountQuery } from "../../service/notification-service";
import { setUnreadCount } from "../../stores/slices/notification-slice";
import { useRef } from "react";
import MobileCustomerTopHeader from "./customer-sidemenu/MobileCustomerTopHeader";

// Isolated component so SignalR state changes (Connecting/Connected/Disconnected)
// do NOT cause CustomerLayout to re-render.
const NotificationHubInitializer: React.FC = () => {
	useNotificationHub();
	return null;
};

const CustomerLayout: React.FC = () => {
	const location = useLocation();
	const navigate = useNavigate();

	const [activeMenu, setActiveMenu] = useState<string>("dashboard");
	const [activeSubMenu, setActiveSubMenu] = useState<string | undefined>();
	const dispatch = useDispatch();

	const { data: userInfo } = useGetUserInfoQuery(undefined, {
		selectFromResult: ({ data }) => ({ data }),
	});

	
	const { data: initialUnreadCount } = useGetUnreadNotificationsCountQuery(undefined, {
		selectFromResult: ({ data }) => ({ data }),
	});

	// Seed unread count from API — only once on initial load, never override after that
	const seededRef = useRef(false);
	useEffect(() => {
		if (initialUnreadCount !== undefined && !seededRef.current) {
			seededRef.current = true;
			dispatch(setUnreadCount(initialUnreadCount));
		}
	}, [initialUnreadCount, dispatch]);

	useEffect(() => {
		const currentPath = location.pathname;

		if (currentPath === "/") {
			setActiveMenu("dashboard");
			setActiveSubMenu(undefined);
			return;
		}

		for (const item of menuItems) {
			if (!item.path) {
				if (item.subMenuItems) {
					for (const subItem of item.subMenuItems) {
						if (subItem.path && (currentPath === subItem.path || currentPath.startsWith(subItem.path + "/"))) {
							setActiveMenu(item.id);
							setActiveSubMenu(subItem.id);
							return;
						}
					}
				}
				continue;
			}

			if (currentPath === item.path) {
				setActiveMenu(item.id);
				setActiveSubMenu(undefined);
				return;
			}

			if (currentPath.startsWith(item.path + "/")) {
				setActiveMenu(item.id);

				if (item.subMenuItems) {
					const matchingSubItem = item.subMenuItems.find(sub => sub.path && (currentPath === sub.path || currentPath.startsWith(sub.path + "/")));

					if (matchingSubItem) {
						setActiveSubMenu(matchingSubItem.id);
					} else {
						setActiveSubMenu(undefined);
					}
				} else {
					setActiveSubMenu(undefined);
				}
				return;
			}

			if (item.subMenuItems) {
				for (const subItem of item.subMenuItems) {
					if (subItem.path && (currentPath === subItem.path || currentPath.startsWith(subItem.path + "/"))) {
						setActiveMenu(item.id);
						setActiveSubMenu(subItem.id);
						return;
					}
				}
			}
		}

		// This will reset the active menu and submenu when the user navigates to a page that is not in the menu
		setActiveMenu("");
		setActiveSubMenu(undefined);
	}, [location.pathname]);

	const lastSyncedEmailRef = useRef<string | null>(null);
	useEffect(() => {
		if (userInfo && userInfo.email !== lastSyncedEmailRef.current) {
			lastSyncedEmailRef.current = userInfo.email;
			dispatch(
				updateCustomerInfo({
					firstName: userInfo.firstName,
					lastName: userInfo.lastName,
					countryCode: userInfo.customerDetails.countryCode,
					phoneNumber: userInfo.phoneNumber,
					hasPin: userInfo.hasPin,
					image: userInfo.image,
					email: userInfo.email,
					customerType: userInfo.customerDetails?.customerType,
				})
			);
		}
	}, [userInfo]);

	const activeMenuItem = menuItems.find(item => item.id === activeMenu);
	const hasActiveSubMenu = !!activeMenuItem?.subMenuItems || false;

	const handleMenuClick = useCallback((menuId: string) => {
		setActiveMenu(menuId);
		setActiveSubMenu(undefined);
	}, []);

	const handleSubMenuClick = useCallback((subMenuId: string) => {
		setActiveSubMenu(subMenuId)}, []);
	

	const handleOnLogOutClicked = useCallback(() => {
		dispatch(logOut());
		navigate(ApplicationRoutes.CUSTOMER_SIGNIN);
	}, [dispatch, navigate]);

	return (
		<div className={`${styles.layout} ${hasActiveSubMenu ? styles.hasSubmenu : ""}`}>
			<NotificationHubInitializer />
			<NotificationListener />

			{/* Desktop Sidebar Menu */}
			<div className={styles.desktopMenu}>
				<CustomerSideMenu
					activeMenu={activeMenu}
					activeSubMenu={activeSubMenu}
					onMenuClick={handleMenuClick}
					onSubMenuClick={handleSubMenuClick}
					onLogOutClick={handleOnLogOutClicked}
					userName={`${userInfo?.firstName} ${userInfo?.lastName}`}
					userEmail={userInfo?.email}
					userAvatar={userInfo?.image ?? ""}
				/>
			</div>

			{/* Mobile Menu */}
			<div className={styles.mobileMenu}>
				<MobileCustomerTopHeader
					activeMenu={activeMenu}
					activeSubMenu={activeSubMenu}
					onMenuClick={handleMenuClick}
					onSubMenuClick={handleSubMenuClick}
					onLogOutClick={handleOnLogOutClicked}
					userName={`${userInfo?.firstName} ${userInfo?.lastName}`}
					userEmail={userInfo?.email}
					userAvatar={userInfo?.image ?? ""}
				/>
			</div>

			{/* Main Content */}
			<main className={styles.main}>
				<div className={styles.content}>
					<Outlet />
				</div>
			</main>
		</div>
	);
};

export default CustomerLayout;
